import { useState } from 'react';
import { Button, Checkbox, Flex, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatStock } from '@app/shared';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { useWindowManager } from '@/shared/lib/windows';
import { useListCategoriesQuery } from '@/entities/category';
import type { CategoryDto } from '@/entities/category';

export interface CategoryGroupWindowProps {
  id: string;
  onClose: () => void;
}

const COLUMNS: ColumnsType<CategoryDto> = [
  { title: 'Название', dataIndex: 'name' },
  { title: 'N', dataIndex: 'pcsPerBox', width: 60, align: 'right' },
  { title: 'Опт. цена', dataIndex: 'wholesalePrice', width: 100, align: 'right', render: (v: string | null) => v ?? '—' },
  { title: 'Остаток', width: 140, render: (_, c) => (c.stock ? formatStock(c.stock) : '—') },
  { title: 'Новинка', dataIndex: 'isNew', width: 80, render: (v: boolean) => (v ? <Tag color="blue">новинка</Tag> : null) },
  { title: 'Акция', dataIndex: 'isPromo', width: 80, render: (v: boolean) => (v ? <Tag color="orange">акция</Tag> : null) },
  { title: 'Архив', dataIndex: 'isArchived', width: 70, render: (v: boolean) => (v ? <Tag>архив</Tag> : null) },
];

/** §2.1: плавающее окно группы — основные категории внутри корневой группы `id`, открывается
 *  двойным кликом по строке группы на `CategoriesPage`. Перемещается/растягивается как любое
 *  другое окно (`FloatingWindow`), можно держать несколько групп открытыми одновременно. */
export function CategoryGroupWindow({ id, onClose }: CategoryGroupWindowProps) {
  const windows = useWindowManager();
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data = [], isFetching } = useListCategoriesQuery({ includeArchived });

  const root = data.find((c) => c.id === id);
  const mains = data.filter((c) => c.parentId === id);

  return (
    <FloatingWindow kind="categoryGroup" title={`Группа: ${root?.name ?? ''}`} onClose={onClose}>
      <Flex vertical gap={8} style={{ height: '100%' }}>
        <Flex justify="space-between" align="center">
          <Button type="primary" onClick={() => windows.open({ kind: 'categoryCreate', parentId: id })}>
            Новая категория
          </Button>
          <Checkbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}>
            Показать архивные
          </Checkbox>
        </Flex>

        <Table<CategoryDto>
          size="small"
          rowKey="id"
          loading={isFetching}
          dataSource={mains}
          columns={COLUMNS}
          pagination={false}
          rowClassName={(c) => (c.isArchived ? 'row-draft' : '')}
          onRow={(record) => ({ onDoubleClick: () => windows.open({ kind: 'category', id: record.id }) })}
        />
      </Flex>
    </FloatingWindow>
  );
}
